/**
 * Block-specific reducer helpers for geometry/create-block-definition and
 * geometry/explode-insert command handling. Extracted from geometryReducer.ts
 * to keep that file under the 2000-LOC CLAUDE.md soft limit.
 *
 * Public surface:
 *   - explodeBlockInsertInDocument(document, insert, blockDef) -> DrawingDocument
 *
 * The math mirrors packages/command-engine/src/blockMutations.ts so callers
 * get consistent behaviour whether they go through the mutation (which
 * returns EntityPreview objects for the shell) or the reducer (which needs
 * DrawingEntity shapes for the persisted document).
 */

import {
  assembleDrawingDocument,
  blockDefinitionSchema,
  blockRecordSchema,
  coerceColor,
  type BlockChildEntity,
  type BlockDefinition,
  type BlockInsertEntity,
  type BlockRecord,
  type DrawingDocument,
  type DrawingEntity,
  type EntityColor,
} from "@dxf-ai-editor/drawing-model";
import {
  composeNestedInsertPlacement,
  computeExplodedEllipseGeometry,
  createBlockDefinitionMutation,
  explodeBlockChildPolylineTableOle,
  initCustomPropertiesFromMetadata,
  guardScale,
  liftNestedInsertAttribute,
  liftNestedInsertBlockrefContext,
  resolveExplodedAttribPlacement,
  resolveMinsertCellOffset,
  stripExplodedInsertRepresentationHandles,
  summarizeExplodeInsertRefusal,
} from "@dxf-ai-editor/command-engine";

// ── 2D affine matrix ({a,b,c,d,e,f} column layout) ─────────────────
// Same LAYOUT as blockMatrix.ts in the scene renderer, but composed in
// MODEL space (CCW-positive rotation — the command-engine
// composeInsertMatrix convention; the render copy's negated-rotation SVG
// sign is a rendering concern). Duplicated here to keep this reducer
// helper self-contained.
type Mat = { a: number; b: number; c: number; d: number; e: number; f: number };

function mat(
  a: number,
  b: number,
  c: number,
  d: number,
  e: number,
  f: number,
): Mat {
  return { a, b, c, d, e, f };
}

function multiply(left: Mat, right: Mat): Mat {
  return mat(
    left.a * right.a + left.c * right.b,
    left.b * right.a + left.d * right.b,
    left.a * right.c + left.c * right.d,
    left.b * right.c + left.d * right.d,
    left.a * right.e + left.c * right.f + left.e,
    left.b * right.e + left.d * right.f + left.f,
  );
}

function transformPoint(m: Mat, p: { x: number; y: number }) {
  return {
    x: m.a * p.x + m.c * p.y + m.e,
    y: m.b * p.x + m.d * p.y + m.f,
  };
}

function transformVector(m: Mat, v: { x: number; y: number }) {
  return {
    x: m.a * v.x + m.c * v.y,
    y: m.b * v.x + m.d * v.y,
  };
}

// SL-15 — the 3D ellipse-explode math (major-axis z, center world z, 3D-conjugate
// ratio, OCS extrusion normal via the INSERT linear part) lives in the shared
// ONE-TRUTH `computeExplodedEllipseGeometry` (@dxf-ai-editor/command-engine), so
// this reducer arm and the shell-preview mutation (blockMutations.ts) cannot
// diverge (shell↔doc parity). The Ellipse case below is a thin adapter that maps
// the plain geometry result into a typed `DrawingEntity`.

/**
 * Compose the INSERT's transform matrix in MODEL space:
 *   T(insertion) × R(+rotDeg) × S(sx, sy) × T(-basePoint)
 *
 * Identical to command-engine composeInsertMatrix (its doc carries the full
 * rationale). `+rotDeg` — CCW-positive DXF/AutoCAD convention (ezdxf
 * `Insert.matrix44()`); @codex round-20 — the previous `-rotDeg` was copied
 * from `blockMatrix.ts`, whose negation is an SVG-authoring artifact (parent
 * `scale(1,-1)` group in the scene renderer). Model space has no such
 * wrapper, so the flipped sign rotated exploded child geometry CW for a CCW
 * INSERT — opposite to the pre-explode render, the (+θ) MINSERT grid step,
 * and the composed scalar rotation fields below.
 */
function composeInsertMatrix(input: {
  translate: { x: number; y: number };
  rotateDeg: number;
  scaleX: number;
  scaleY: number;
  basePoint?: { x: number; y: number };
}): Mat {
  const { translate: t, rotateDeg, scaleX, scaleY, basePoint } = input;
  const rad = (rotateDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const tMat = mat(1, 0, 0, 1, t.x, t.y);
  const rMat = mat(cos, sin, -sin, cos, 0, 0);
  const sMat = mat(scaleX, 0, 0, scaleY, 0, 0);
  const bx = basePoint?.x ?? 0;
  const by = basePoint?.y ?? 0;
  const bMat = mat(1, 0, 0, 1, -bx, -by);
  return multiply(tMat, multiply(rMat, multiply(sMat, bMat)));
}

// ── BYBLOCK / BYLAYER inheritance ─────────────────────────────
// Layer "0" inside a block inherits the parent INSERT's layer. Every
// other layer value is kept verbatim.
function resolveChildLayer(childLayer: string, parentLayer: string): string {
  return childLayer === "0" ? parentLayer : childLayer;
}

// ByBlock inherits from the parent INSERT. ByLayer is passed through —
// the actual layer lookup happens at render time, not during explode.
function resolveChildProp(childValue: string, parentValue: string): string {
  if (childValue === "ByBlock") return parentValue;
  return childValue;
}

/**
 * EPIC-COLOR-UNION S1 — colour ByBlock-inheritance on the typed union (here the
 * resolved colour is spread DIRECTLY into a new `DrawingEntity`, so it must
 * REMAIN the union, unlike the command-engine explode which feeds a string
 * property bag). A `byBlock` child inherits the parent INSERT colour; every
 * other case keeps the child's own colour verbatim.
 *
 * Both operands are `coerceColor`-guarded first: `assembleDrawingDocument` does
 * NOT re-run the schema preprocess in PROD, so older documents / reducer-built
 * block definitions can still reach EXPLODE with a legacy STRING colour. A raw
 * "ByBlock" string has no `.kind`, so without coercion it would NOT inherit the
 * parent INSERT colour and the exploded child would carry a raw string — the
 * command-engine explode path coerces identically.
 */
function resolveChildColor(
  childColor: EntityColor,
  parentColor: EntityColor,
): EntityColor {
  const child = coerceColor(childColor);
  return child.kind === "byBlock" ? coerceColor(parentColor) : child;
}

function cloneBaseProps(
  child: BlockChildEntity,
  parentInsert: BlockInsertEntity,
) {
  return {
    layer: resolveChildLayer(child.layer, parentInsert.layer),
    color: resolveChildColor(child.color, parentInsert.color),
    linetype: resolveChildProp(child.linetype, parentInsert.linetype),
    lineweight: resolveChildProp(child.lineweight, parentInsert.lineweight),
    transparency: child.transparency,
  };
}

function cellInsertPoint(
  parent: BlockInsertEntity,
  row: number,
  col: number,
): { x: number; y: number } {
  // @codex round-18 P2 (r3570000118) — mirrors the command-engine shell
  // mutation (`packages/command-engine/src/blockMutations.ts`
  // `cellInsertPoint`): the per-cell grid step rides the block's own
  // rotate+scale (render truth `transformBlockDirection`, shared via
  // `resolveMinsertCellOffset`) — the SAME residual the WORLD ATTRIB regime
  // below already uses (#482). Before this fix this origin fed the
  // child-geometry/block-local-ATTRIB `matrix` with the RAW unrotated
  // `(col·columnSpacing, row·rowSpacing)` step, so a rotated/scaled MINSERT
  // persisted the geometry at the wrong cell while the WORLD ATTRIB label
  // (already using the rotated offset) landed correctly — splitting the
  // label from its geometry in the reconciled document. ONE-TRUTH with the
  // WORLD ATTRIB cellDelta: same helper, same inputs, so shell preview and
  // persisted document can never diverge from EACH OTHER or from render.
  const offset = resolveMinsertCellOffset({
    row,
    col,
    columnSpacing: parent.columnSpacing,
    rowSpacing: parent.rowSpacing,
    rotationDeg: parent.rotation,
    scaleX: parent.scaleX,
    scaleY: parent.scaleY,
  });
  return {
    x: parent.insertionPoint.x + offset.x,
    y: parent.insertionPoint.y + offset.y,
  };
}

/**
 * Explode a single block child into one or more fully-formed
 * DrawingEntity objects ready to be appended to the document. Handles
 * Circle/Arc → Ellipse promotion for non-uniform scale, and keeps nested
 * INSERTs as INSERTs (AutoCAD single-level behaviour).
 */
export function explodeChild(params: {
  child: BlockChildEntity;
  matrix: Mat;
  parentInsert: BlockInsertEntity;
  parentRotationDeg: number;
  scaleX: number;
  scaleY: number;
  // SL-15 F3: the INSERT's z-scale. The 2D `matrix` (composeInsertMatrix) does
  // not carry z, but the linear part's z-component of a DIRECTION VECTOR (e.g.
  // the ellipse major axis) scales purely by scaleZ — rotation about Z and the
  // XY scale never mix z into XY. Threaded so the ellipse arm can preserve a
  // tilted (group-31) major axis instead of flattening it to the XY plane.
  scaleZ: number;
  // SL-15 F4: the block definition's base-point z (DXF 30). The 2D `matrix`
  // subtracts basePoint x/y before scaling (composeInsertMatrix `bMat`); the
  // z of a POSITION follows the same convention, so the ellipse arm needs the
  // base z to compose a child center's world z. `blockDef.basePoint` is a full
  // 3D point (drawingPoint3DSchema) so this is always defined at the caller.
  basePointZ: number;
  allocateId: () => string;
}): DrawingEntity[] {
  const {
    child,
    matrix,
    parentInsert,
    parentRotationDeg,
    scaleX,
    scaleY,
    scaleZ,
    basePointZ,
    allocateId,
  } = params;
  const base = cloneBaseProps(child, parentInsert);
  const uniform = Math.abs(Math.abs(scaleX) - Math.abs(scaleY)) < 1e-9;

  switch (child.type) {
    case "Line": {
      const start = transformPoint(matrix, child.start);
      const end = transformPoint(matrix, child.end);
      return [
        {
          id: allocateId(),
          type: "Line",
          spaceId: parentInsert.spaceId,
          ...base,
          start: { x: start.x, y: start.y },
          end: { x: end.x, y: end.y },
          thickness: child.thickness,
        } as DrawingEntity,
      ];
    }
    case "Circle": {
      const c = transformPoint(matrix, child.center);
      if (uniform) {
        return [
          {
            id: allocateId(),
            type: "Circle",
            spaceId: parentInsert.spaceId,
            ...base,
            center: { x: c.x, y: c.y },
            radius: child.radius * Math.abs(scaleX),
            thickness: child.thickness,
          } as DrawingEntity,
        ];
      }
      // Non-uniform → Ellipse.
      const majorLocal = { x: child.radius, y: 0 };
      const majorWorld = transformVector(matrix, majorLocal);
      return [
        {
          id: allocateId(),
          type: "Ellipse",
          spaceId: parentInsert.spaceId,
          ...base,
          center: { x: c.x, y: c.y },
          majorAxisEndpoint: majorWorld,
          ratio: Math.abs(scaleY) / Math.abs(scaleX),
          startParam: 0,
          endParam: 2 * Math.PI,
          thickness: child.thickness,
          // SL-15 round-10 @codex F1 (P1) — set the (0,0,1) schema default HERE at
          // the producer: this Ellipse is built via `as DrawingEntity` (no Zod), and
          // `assembleDrawingDocument`'s PROD fast-path skips the parse that would inject
          // the default → `extrusion` would be `undefined` at runtime and every reader
          // (dxfGeometry buildEllipsePreview, ellipseFromEntity, …) would have to guard
          // it. Setting it by construction closes the class. A planar (0,0,1) normal is
          // byte-identical in DXF export (the writer omits 210 for the identity normal).
          extrusion: { x: 0, y: 0, z: 1 },
        } as DrawingEntity,
      ];
    }
    case "Arc": {
      const c = transformPoint(matrix, child.center);
      if (uniform) {
        return [
          {
            id: allocateId(),
            type: "Arc",
            spaceId: parentInsert.spaceId,
            ...base,
            center: { x: c.x, y: c.y },
            radius: child.radius * Math.abs(scaleX),
            startAngle: child.startAngle + parentRotationDeg,
            endAngle: child.endAngle + parentRotationDeg,
            thickness: child.thickness,
          } as DrawingEntity,
        ];
      }
      const majorLocal = { x: child.radius, y: 0 };
      const majorWorld = transformVector(matrix, majorLocal);
      const startRad = (child.startAngle * Math.PI) / 180;
      const endRad = (child.endAngle * Math.PI) / 180;
      return [
        {
          id: allocateId(),
          type: "Ellipse",
          spaceId: parentInsert.spaceId,
          ...base,
          center: { x: c.x, y: c.y },
          majorAxisEndpoint: majorWorld,
          ratio: Math.abs(scaleY) / Math.abs(scaleX),
          startParam: startRad,
          endParam: endRad,
          thickness: child.thickness,
          // SL-15 round-10 @codex F1 (P1) — set the (0,0,1) schema default at the
          // producer (see the Circle→Ellipse branch above): PROD assembly skips Zod so
          // an unset `extrusion` would be `undefined` at runtime. Byte-identical export.
          extrusion: { x: 0, y: 0, z: 1 },
        } as DrawingEntity,
      ];
    }
    case "Ellipse": {
      // Thin adapter over the shared ONE-TRUTH 3D ellipse-explode math (F3
      // major-axis z, F4/F7 center world z, F5 3D-conjugate ratio, F6/F8 OCS
      // extrusion normal via the INSERT linear part). The SAME helper backs the
      // shell-preview mutation (blockMutations.ts) so the transient EXPLODE
      // preview and this reconciled document are byte-identical.
      const geom = computeExplodedEllipseGeometry(
        {
          center: child.center,
          majorAxisEndpoint: child.majorAxisEndpoint,
          ratio: child.ratio,
          extrusion: child.extrusion,
          // Params re-anchor to the recovered principal frame under a rotated /
          // non-uniform INSERT (SL-15 round-8 @codex F2); a full ellipse /
          // frame-preserving case returns them byte-identical.
          startParam: child.startParam,
          endParam: child.endParam,
        },
        {
          matrix,
          scaleZ,
          insertionZ: parentInsert.insertionPoint.z ?? 0,
          basePointZ,
        },
      );
      return [
        {
          id: allocateId(),
          type: "Ellipse",
          spaceId: parentInsert.spaceId,
          ...base,
          center: geom.center,
          majorAxisEndpoint: geom.majorAxisEndpoint,
          ratio: geom.ratio,
          startParam: geom.startParam ?? child.startParam,
          endParam: geom.endParam ?? child.endParam,
          thickness: child.thickness,
          // SL-15 round-10 @codex F1 (P1) — ALWAYS set the plane normal at the
          // producer (no `undefined` reaches a reader): the default (0,0,1) for a
          // planar child, else the recovered OCS normal. Byte-identical DXF export —
          // the ellipse writer omits group 210 for the identity (0,0,1) normal, so a
          // planar child under an orientation-preserving INSERT emits no 210/220/230.
          extrusion: geom.extrusionIsDefault
            ? { x: 0, y: 0, z: 1 }
            : geom.extrusion,
        } as DrawingEntity,
      ];
    }
    case "LWPOLYLINE": {
      const vertices = child.vertices.map((v) => {
        const t = transformPoint(matrix, { x: v.x, y: v.y });
        return {
          x: t.x,
          y: t.y,
          bulge: v.bulge,
          startWidth: v.startWidth,
          endWidth: v.endWidth,
        };
      });
      return [
        {
          id: allocateId(),
          type: "LWPOLYLINE",
          spaceId: parentInsert.spaceId,
          ...base,
          vertices,
          closed: child.closed,
          constantWidth: child.constantWidth,
          elevation: child.elevation,
          thickness: child.thickness,
        } as DrawingEntity,
      ];
    }
    case "Text": {
      const t = transformPoint(matrix, child.insertionPoint);
      return [
        {
          id: allocateId(),
          type: "Text",
          spaceId: parentInsert.spaceId,
          ...base,
          insertionPoint: t,
          height: child.height * Math.abs(scaleX),
          rotation: child.rotation + parentRotationDeg,
          contents: child.contents,
          styleName: child.styleName,
          widthFactor: child.widthFactor,
          obliqueAngle: child.obliqueAngle,
          hJustification: child.hJustification,
          vJustification: child.vJustification,
          thickness: child.thickness,
        } as DrawingEntity,
      ];
    }
    case "MText": {
      const t = transformPoint(matrix, child.insertionPoint);
      return [
        {
          id: allocateId(),
          type: "MText",
          spaceId: parentInsert.spaceId,
          ...base,
          insertionPoint: t,
          nominalHeight: child.nominalHeight * Math.abs(scaleX),
          referenceWidth: child.referenceWidth * Math.abs(scaleX),
          attachmentPoint: child.attachmentPoint,
          drawingDirection: child.drawingDirection,
          contents: child.contents,
          styleName: child.styleName,
          rotation: child.rotation + parentRotationDeg,
          lineSpacingStyle: child.lineSpacingStyle,
          lineSpacingFactor: child.lineSpacingFactor,
          backgroundFill: child.backgroundFill,
        } as DrawingEntity,
      ];
    }
    case "Block": {
      // Nested INSERT. One-level EXPLODE keeps it as an INSERT — the user must
      // EXPLODE again to dive deeper (matches AutoCAD). ONE-TRUTH placement math
      // shared with the shell-preview mutation (blockMutations.ts) so the two
      // paths cannot diverge (shell↔doc parity): the 3-axis scale COMPOSES
      // (scaleZ was a child-drop of the parent Z scale before), rotation
      // composes, spacing stays verbatim (the render re-applies the composed
      // scale to the MINSERT step — see `nestedInsertExplode.ts`).
      const placement = composeNestedInsertPlacement({
        child,
        matrix,
        parentRotationDeg,
        scaleX,
        scaleY,
        scaleZ,
      });
      return [
        {
          id: allocateId(),
          type: "Block",
          spaceId: parentInsert.spaceId,
          ...base,
          blockName: child.blockName,
          insertionPoint: placement.insertionPoint,
          scaleX: placement.scaleX,
          scaleY: placement.scaleY,
          scaleZ: placement.scaleZ,
          rotation: placement.rotation,
          columnCount: placement.columnCount,
          rowCount: placement.rowCount,
          columnSpacing: placement.columnSpacing,
          rowSpacing: placement.rowSpacing,
          // DEFAULT (0,0,1) OCS normal — the re-emitted placement is expressed
          // in WCS: `composeNestedInsertPlacement` lifts the child insertion
          // through the parent 2D affine `matrix` and composes scale/rotation
          // WITHOUT applying the child's OWN OCS extrusion (matching the scene3d
          // flatten, which ignores extrusion at every level). Re-declaring
          // `child.extrusion` on a WCS-framed placement makes the OCS-aware
          // consumers (PDF `insertConverter.ocsOuterMatrix`, DXF export
          // 210/220/230) apply the OCS basis as the OUTERMOST map about the
          // world origin (`E·parentMatrix·insertMatrix`), whereas the
          // pre-explode nested render applied it BETWEEN the parent and child
          // (`parentMatrix·E·insertMatrix`) — an order-swap that moves/rotates a
          // tilted nested block wrong on plot/export (@codex round-20 F2). The
          // child's own OCS tilt is not carried through explode (an
          // S10-XFORM/OCS residual, same as the flatten's extrusion no-op).
          extrusion: { x: 0, y: 0, z: 1 },
          // #470 (Lane F) world-vs-block-local — a nested INSERT's OWN ATTRIBs
          // travel WITH it (a nested INSERT may itself carry ATTRIBs). A SET
          // `overridePosition` is a world point in the ENCLOSING block frame, so
          // lift it through the parent affine (never dropped/doubled — the
          // nested #470 twin); an UNSET one rides through verbatim.
          attributes: (child.attributes ?? []).map((attrib) =>
            liftNestedInsertAttribute(attrib, {
              matrix,
              scaleX,
              scaleY,
              parentRotationDeg,
            }),
          ),
          // Preserve dynamic-block + annotative child data (was dropped — the
          // "child-drop" class this slice closes).
          customProperties: child.customProperties ?? {},
          annotationScales: child.annotationScales ?? [],
          // WORLD-LIFT the per-scale `blockref` contexts (@codex round-21 F1):
          // the render applies a matched annotative context as ABSOLUTE
          // top-level insertion/scale/rotation overrides, so a verbatim copy
          // would teleport the re-emitted top-level INSERT to the RAW nested
          // coordinates when that CANNOSCALE is active. Compose each through the
          // SAME parent affine as the base placement (and clear the stale
          // ext-dict FK — export re-mints).
          annotativeContexts: (child.annotativeContexts ?? []).map((context) =>
            liftNestedInsertBlockrefContext(context, {
              matrix,
              scaleX,
              scaleY,
              parentRotationDeg,
            }),
          ),
          ...(child.visibilityState !== undefined
            ? { visibilityState: child.visibilityState }
            : {}),
          // Preserve the parameter/visibility divergence but STRIP the
          // round-trip-only representation LINKAGE handle + etag (@codex
          // round-21 F3): the source nested INSERT stays in the still-exported
          // block definition, so reusing `representationDataHandle` /
          // `representationEtag` would point TWO block references at ONE
          // per-INSERT representation object. Copying or minting a handle is
          // worse (invalid dup-owner DXF / dangling 360) — independent DXF
          // re-serialisation of the exploded INSERT's own representation is a
          // KNOWN LIMITATION pending the Phase N.4 export-side
          // ACDB_BLOCKREPRESENTATION_DATA materialisation (@codex round-22 P2;
          // see `nestedInsertExplode.stripExplodedInsertRepresentationHandles`
          // doc). `parameterOverrides` stay in the typed model.
          ...(child.representationOverrides !== undefined
            ? {
                representationOverrides: stripExplodedInsertRepresentationHandles(
                  child.representationOverrides,
                ),
              }
            : {}),
          ...(child.creationScaleName !== undefined
            ? { creationScaleName: child.creationScaleName }
            : {}),
        } as DrawingEntity,
      ];
    }
    case "Polyline":
    case "Table":
    case "OLE Frame": {
      // The three legacy primitives `blockChildEntitySchema` admits and this
      // arm used to DELETE from the `default:` below while
      // `explodeBlockInsertInDocument` removed the parent INSERT regardless
      // (`abi1-test.dxf` alone carries eleven heavy POLYLINEs inside block
      // definitions) — @codex PR #785 round 13, FINDING 1. ONE carry affine +
      // ONE expressibility truth with the shell audit row — `command-engine/
      // explodeBlockChildPolylineTableOle.ts`, whose header carries the whole
      // derivation. `explodeBlockInsertInDocument` has already declined the
      // whole INSERT when any child is not carryable, so reaching here means
      // it is.
      //
      // `...base` is spread LAST on purpose, unlike every arm above: those
      // build a fresh literal and can put it anywhere, while this one SPREADS
      // the carried entity (it must — `contentGroups`, `binaryChunks`,
      // per-vertex XDATA and the unmapped-group passthrough are user content a
      // field allow-list would delete), so the resolved
      // layer/colour/linetype/lineweight/transparency has to override the
      // child's own authored values rather than be overridden by them.
      const carried = explodeBlockChildPolylineTableOle(
        child,
        (p) => transformPoint(matrix, p),
        { rotationDeg: parentRotationDeg, scaleX, scaleY },
        {
          scaleZ,
          insertionZ: parentInsert.insertionPoint.z ?? 0,
          basePointZ,
        },
      );
      return [
        {
          ...carried,
          id: allocateId(),
          spaceId: parentInsert.spaceId,
          ...base,
        } as DrawingEntity,
      ];
    }
    default:
      return [];
  }
}

/**
 * Apply an EXPLODE to an INSERT entity inside a DrawingDocument, returning
 * the next document state with the INSERT removed and its child entities
 * appended. Visible ATTRIBs are promoted to Text entities at their
 * (override) positions; invisible ATTRIBs are dropped per AutoCAD.
 */
export function explodeBlockInsertInDocument(
  document: DrawingDocument,
  insert: BlockInsertEntity,
  blockDef: BlockDefinition,
): DrawingDocument {
  // EXPLODE is ALL-OR-NOTHING. A child whose enclosing affine its own fields
  // cannot absorb (an ACAD_TABLE under anything but a proper rotation, an
  // OLE2FRAME under an oblique one — `command-engine/
  // explodeBlockChildPolylineTableOle.ts` derives both boundaries) must not be
  // half-carried, and it must ESPECIALLY not be dropped while the parent INSERT
  // is removed anyway: that is the silent data loss this guard closes. The
  // document is returned UNCHANGED, so both call sites (the
  // `geometry/explode-insert` arm and `geometry/explode-entities`' Block branch,
  // which diffs before→after) produce no mutation at all, and the shell reducer
  // reports the refusal as an RU+EN warning-tone audit row from the SAME
  // predicate — the shell can never report a refusal the document did not make.
  if (
    summarizeExplodeInsertRefusal(blockDef.entities, {
      rotationDeg: insert.rotation,
      scaleX: guardScale(insert.scaleX),
      scaleY: guardScale(insert.scaleY),
    }).refused
  ) {
    return document;
  }

  // First, build a new document without the INSERT so the ID allocator
  // does not collide with its ID.
  let nextDoc: DrawingDocument = assembleDrawingDocument({
    ...document,
    spaces: document.spaces.map((space) => ({
      ...space,
      entities: space.entities.filter((e) => e.id !== insert.id),
    })),
  });

  const rowCount = Math.max(1, insert.rowCount);
  const colCount = Math.max(1, insert.columnCount);
  const parentRot = insert.rotation;
  const baseX = blockDef.basePoint.x;
  const baseY = blockDef.basePoint.y;
  // Render truth (`blockFromEntity.ts` builds `transform.scale` via
  // `guardScale` BEFORE anything consumes it): normalise degenerate scale
  // components ONCE at the entry — mirrors the command-engine shell mutation
  // (shell↔doc parity; @codex round-19/20 guarded-scale class), so child
  // geometry, ATTRIB heights and the grid step all ride the SAME guarded
  // scales the render drew with.
  const scaleX = guardScale(insert.scaleX);
  const scaleY = guardScale(insert.scaleY);
  const scaleZ = guardScale(insert.scaleZ);

  const newEntities: DrawingEntity[] = [];

  // Reserve IDs against a running set so every produced entity has a
  // unique id even though we haven't appended them yet.
  const reservedIds = new Set<string>();
  for (const space of nextDoc.spaces) {
    for (const e of space.entities) reservedIds.add(e.id);
  }
  const allocateId = (): string => {
    let counter = 1;
    let candidate = `entity-${counter}`;
    while (reservedIds.has(candidate)) {
      counter += 1;
      candidate = `entity-${counter}`;
    }
    reservedIds.add(candidate);
    return candidate;
  };

  for (let row = 0; row < rowCount; row++) {
    for (let col = 0; col < colCount; col++) {
      const origin = cellInsertPoint(insert, row, col);
      const matrix = composeInsertMatrix({
        translate: origin,
        rotateDeg: parentRot,
        scaleX,
        scaleY,
        basePoint: { x: baseX, y: baseY },
      });

      // Visible ATTRIBs → Text entities (invisible ATTRIBs are dropped).
      for (const attrib of insert.attributes) {
        if (attrib.invisible) continue;
        const found = blockDef.entities.find(
          (e) => e.type === "Attdef" && e.tag === attrib.tag,
        );
        const matchingAttdef = found?.type === "Attdef" ? found : undefined;
        // #470 (Lane F) — ONE-TRUTH world-vs-block-local placement shared with
        // the shell-preview mutation (shell↔doc parity): a SET `overridePosition`
        // is a WORLD point that rides only the pure grid residual (never the
        // INSERT affine a second time — the pre-#470 double-transform bug); an
        // UNSET one follows the ATTDEF template through the full affine.
        const placement = resolveExplodedAttribPlacement({
          attrib,
          matchingAttdef,
          matrix,
          // #482 — the WORLD residual rides the block's rotate+scale (render
          // truth `transformBlockDirection`), NOT the raw `origin − baseOrigin`
          // step; ONE-TRUTH with the shell-preview mutation via the shared helper.
          cellDelta: resolveMinsertCellOffset({
            row,
            col,
            columnSpacing: insert.columnSpacing,
            rowSpacing: insert.rowSpacing,
            rotationDeg: parentRot,
            scaleX,
            scaleY,
          }),
          parentRotationDeg: parentRot,
          scaleX,
        });
        newEntities.push({
          id: allocateId(),
          type: "Text",
          spaceId: insert.spaceId,
          layer: insert.layer,
          color: insert.color,
          linetype: insert.linetype,
          lineweight: insert.lineweight,
          transparency: insert.transparency,
          insertionPoint: placement.insertionPoint,
          height: placement.height,
          rotation: placement.rotation,
          contents: attrib.value,
          styleName: placement.styleName,
          widthFactor: 1,
          obliqueAngle: 0,
          hJustification: 0,
          vJustification: 0,
          thickness: 0,
          // A materialised ATTRIB → plain single-line TEXT is non-annotative.
          // `annotativeContexts` is REQUIRED on a TextEntity — the compact/
          // virtualised text store reads it without a guard
          // (`compactTextStores.ts` `entity.annotativeContexts.length`), so
          // omitting it threw `Cannot read properties of undefined (reading
          // 'length')` the moment an exploded INSERT+ATTRIB reached the live
          // scene aggregate store (a pre-existing gap this explode path
          // always carried).
          //
          // GitHub Codex review PR #558 round 2 P2 — `generationFlags` is
          // now `placement.generationFlags` (the resolver's own doc has the
          // full render-parity derivation), NOT a hardcoded 0 — a
          // MIRRTEXT=1-mirrored WORLD attribute's DXF group-71 handedness
          // bit must survive EXPLODE, or MIRROR→EXPLODE silently
          // un-mirrored the fully-reflected attribute even though its
          // position/rotation carried over correctly.
          generationFlags: placement.generationFlags,
          // Typed empty literal (not bare `[]` → `never[]`): a `never[]`
          // element type breaks the `as DrawingEntity` comparability check
          // (the literal legitimately omits schema-defaulted fields such as
          // `extrusion`, so the reverse assignability direction is the only
          // one that holds — and `never[]` severs it, tripping TS2352).
          annotativeContexts: [] as Extract<
            DrawingEntity,
            { type: "Text" }
          >["annotativeContexts"],
        } as DrawingEntity);
      }

      for (const child of blockDef.entities) {
        if (child.type === "Attdef") continue; // ATTDEFs don't copy out
        newEntities.push(
          ...explodeChild({
            child,
            matrix,
            parentInsert: insert,
            parentRotationDeg: parentRot,
            scaleX,
            scaleY,
            scaleZ,
            basePointZ: blockDef.basePoint.z,
            allocateId,
          }),
        );
      }
    }
  }

  // Append all new entities to the INSERT's space.
  nextDoc = assembleDrawingDocument({
    ...nextDoc,
    spaces: nextDoc.spaces.map((space) =>
      space.id === insert.spaceId
        ? { ...space, entities: [...space.entities, ...newEntities] }
        : space,
    ),
  });

  return nextDoc;
}


// ═══════════════════════════════════════════════════════════════
// createBlockDefinitionInDocument
// ═══════════════════════════════════════════════════════════════

export interface CreateBlockDefinitionCommandInput {
  blockDef: {
    name: string;
    handle: string;
    ownerHandle?: string;
    layer: string;
    flags: number;
    basePoint: { x: number; y: number; z: number };
    xrefPathName: string;
    description: string;
    entities: unknown[];
  };
  blockRecord: {
    name: string;
    insertionUnits: number;
    explodable: boolean;
    scalable: "uniform" | "non-uniform";
    layoutHandle?: string;
    isAnnotative: boolean;
    description: string;
    dynamicBlockData?: unknown;
  };
  postAction: "retain" | "convert" | "delete";
  sourceEntityIds: string[];
  convertSpaceId?: string;
  convertInsertPoint?: { x: number; y: number };
  convertInsertId?: string;
  // CECOLOR round-6 FINDING C — the current colour for the postAction=convert
  // replacement INSERT instance ONLY (the block DEFINITION stays colour-less).
  // Stamped at the dispatch chokepoint; ByLayer default when absent.
  convertInsertColor?: EntityColor;
  // T7 (agent-bridge) local Codex round-17 P2 — the CLAYER sibling of
  // `convertInsertColor` directly above: the current layer for the
  // postAction=convert replacement INSERT instance ONLY. Stamped at the
  // dispatch chokepoint; "0" default when absent.
  convertInsertLayer?: string;
}

/**
 * Apply a geometry/create-block-definition command: register the new
 * BlockDefinition + BlockRecord in the DrawingDocument, run post-action
 * bookkeeping (retain/convert/delete of source entities), and when
 * postAction=convert append a fresh INSERT entity pointing at the new
 * block. Re-parses input through blockDefinitionSchema /
 * blockRecordSchema for defensive validation.
 */
export function createBlockDefinitionInDocument(
  document: DrawingDocument,
  command: CreateBlockDefinitionCommandInput,
): DrawingDocument {
  const parsedBlockDef: BlockDefinition = blockDefinitionSchema.parse({
    name: command.blockDef.name,
    handle: command.blockDef.handle,
    ...(command.blockDef.ownerHandle !== undefined
      ? { ownerHandle: command.blockDef.ownerHandle }
      : {}),
    layer: command.blockDef.layer,
    flags: command.blockDef.flags,
    basePoint: command.blockDef.basePoint,
    xrefPathName: command.blockDef.xrefPathName,
    description: command.blockDef.description,
    entities: command.blockDef.entities,
  });
  const parsedBlockRecord: BlockRecord = blockRecordSchema.parse({
    name: command.blockRecord.name,
    insertionUnits: command.blockRecord.insertionUnits,
    explodable: command.blockRecord.explodable,
    scalable: command.blockRecord.scalable,
    ...(command.blockRecord.layoutHandle !== undefined
      ? { layoutHandle: command.blockRecord.layoutHandle }
      : {}),
    isAnnotative: command.blockRecord.isAnnotative,
    description: command.blockRecord.description,
    ...(command.blockRecord.dynamicBlockData !== undefined
      ? { dynamicBlockData: command.blockRecord.dynamicBlockData }
      : {}),
  });

  const mutationResult = createBlockDefinitionMutation({
    blockDef: parsedBlockDef,
    blockRecord: parsedBlockRecord,
    postAction: command.postAction,
    sourceEntityIds: command.sourceEntityIds,
    ...(command.convertSpaceId !== undefined
      ? { convertSpaceId: command.convertSpaceId }
      : {}),
    ...(command.convertInsertPoint !== undefined
      ? { convertInsertPoint: command.convertInsertPoint }
      : {}),
    ...(command.convertInsertId !== undefined
      ? { convertInsertId: command.convertInsertId }
      : {}),
    existingBlocks: document.blocks,
  });

  const nextBlocks = {
    ...document.blocks,
    [mutationResult.blockDef.name]: mutationResult.blockDef,
  };
  const nextBlockRecords = {
    ...document.blockRecords,
    [mutationResult.blockRecord.name]: mutationResult.blockRecord,
  };

  // Remove source entities (delete / convert postActions).
  const removedIdSet = new Set(mutationResult.removedEntityIds);
  let nextDoc: DrawingDocument = assembleDrawingDocument({
    ...document,
    blocks: nextBlocks,
    blockRecords: nextBlockRecords,
    spaces: document.spaces.map((space) => ({
      ...space,
      entities: space.entities.filter((e) => !removedIdSet.has(e.id)),
    })),
  });

  // postAction=convert: append a fresh INSERT entity.
  if (
    command.postAction === "convert" &&
    command.convertSpaceId &&
    command.convertInsertPoint &&
    command.convertInsertId
  ) {
    // Seed customProperties from the block's dynamicMetadata so the
    // converted INSERT is immediately addressable by DYNSET / property
    // palette. Static blocks end up with `{}` (no metadata → helper
    // returns an empty map).
    const insertEntity: DrawingEntity = {
      id: command.convertInsertId,
      type: "Block",
      spaceId: command.convertSpaceId,
      blockName: mutationResult.blockDef.name,
      // T7 (agent-bridge) local Codex round-17 P2 — the convert replacement
      // INSERT inherits the current LAYER (stamped at the dispatch
      // chokepoint into `convertInsertLayer`), matching the regular
      // create-block-insert path. "0" default when no current layer was
      // stamped (legacy callers).
      layer: command.convertInsertLayer ?? "0",
      // EPIC-COLOR-UNION S1 — typed colour union default.
      // CECOLOR round-6 FINDING C — the convert replacement INSERT inherits the
      // current colour (stamped at the dispatch chokepoint into
      // `convertInsertColor`), matching the regular create-block-insert path.
      // ByLayer default when no current colour was stamped (legacy callers).
      color: command.convertInsertColor ?? { kind: "byLayer" },
      linetype: "ByLayer",
      lineweight: "ByLayer",
      transparency: "0",
      insertionPoint: {
        x: command.convertInsertPoint.x,
        y: command.convertInsertPoint.y,
      },
      scaleX: 1,
      scaleY: 1,
      scaleZ: 1,
      rotation: 0,
      columnCount: 1,
      rowCount: 1,
      columnSpacing: 0,
      rowSpacing: 0,
      extrusion: { x: 0, y: 0, z: 1 },
      attributes: [],
      customProperties: initCustomPropertiesFromMetadata(
        mutationResult.blockDef.dynamicMetadata,
      ),
      annotationScales: [],
      annotativeContexts: [],
    };

    nextDoc = assembleDrawingDocument({
      ...nextDoc,
      spaces: nextDoc.spaces.map((space) =>
        space.id === command.convertSpaceId
          ? { ...space, entities: [...space.entities, insertEntity] }
          : space,
      ),
    });
  }

  return nextDoc;
}
